import type {
  IFilterOptions,
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type {
  CloningStatus,
  GitRootsFiltersInput,
  ResourceState,
} from "gql/graphql";
import type { IGitRootEventsData } from "pages/group/scope/types";
import { useFiltersStorage } from "storage/filters";
import { getCheckedFilter, getCheckedFilters } from "utils/filters";
import { formatBoolean } from "utils/format-helpers";

const useGitRootsFilters = ({
  gitNickNames,
  gitBranches,
  groupName,
  loading,
  setFilters,
}: Readonly<{
  gitNickNames: string[];
  gitBranches: string[];
  groupName: string;
  loading: boolean;
  setFilters: (filters: GitRootsFiltersInput) => void;
}>): IUseFilterProps<IGitRootEventsData> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const nicknameOptions = useMemo((): IOptions[] => {
    return Array.from(
      new Map(
        gitNickNames.map((nickname): [string, IOptions] => [
          nickname,
          { label: nickname, value: nickname },
        ]),
      ).values(),
    );
  }, [gitNickNames]);

  const branchOptions = useMemo((): IOptions[] => {
    return Array.from(
      new Map(
        gitBranches.map((branch): [string, IOptions] => [
          branch,
          { label: branch, value: branch },
        ]),
      ).values(),
    );
  }, [gitBranches]);

  const options = useMemo(
    (): IOptionsProps<IGitRootEventsData>[] => [
      {
        filterOptions: [
          {
            key: "nickname",
            label: t("group.scope.git.repo.nickname.text"),
            options: nicknameOptions,
            type: "checkboxes",
          },
        ],
        label: t("group.scope.git.repo.nickname.text"),
      },
      {
        filterOptions: [
          {
            key: "branch",
            label: t("group.scope.git.repo.branch.header"),
            options: branchOptions,
            type: "checkboxes",
          },
        ],
        label: t("group.scope.git.repo.branch.header"),
      },
      {
        filterOptions: [
          {
            key: "state",
            label: t("group.scope.common.state"),
            options: [
              { label: "Active", value: "ACTIVE" },
              { label: "Inactive", value: "INACTIVE" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.scope.common.state"),
      },
      {
        filterOptions: [
          {
            key: "status",
            label: t("group.scope.git.repo.cloning.status"),
            options: [
              { label: "Cloning", value: "CLONING" },
              { label: "Failed", value: "FAILED" },
              { label: "Cloned", value: "OK" },
              { label: "Queued", value: "QUEUED" },
              { label: "Unknown", value: "UNKNOWN" },
            ],
            type: "checkboxes",
          },
        ],
        label: t("group.scope.git.repo.cloning.status"),
      },
      {
        filterOptions: [
          {
            key: "includesHealthCheck",
            label: t("group.scope.git.healthCheck.tableHeader"),
            options: [
              { label: formatBoolean(true), value: "true" },
              { label: formatBoolean(false), value: "false" },
            ],
            type: "radioButton",
          },
        ],
        label: t("group.scope.git.healthCheck.tableHeader"),
      },
    ],
    [t, nicknameOptions, branchOptions],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("git_roots", groupName) as string | null;
  }, [getValues, groupName]);
  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("git_roots", value, groupName);
    },
    [setValues, groupName],
  );

  const handleFilterChange = useCallback(
    (newFilters: readonly IFilterOptions<IGitRootEventsData>[]): void => {
      if (loading) return;
      const newFiltersArray =
        newFilters as IFilterOptions<IGitRootEventsData>[];
      const nicknamesChecked = getCheckedFilters(newFiltersArray, "nickname");
      const branchesChecked = getCheckedFilters(newFiltersArray, "branch");
      const stateValue = getCheckedFilter(newFiltersArray, "state")?.value;
      const allStatuses = getCheckedFilters(newFiltersArray, "status");
      const backendStatuses = Array.isArray(allStatuses)
        ? allStatuses.filter((status): boolean => status !== "OPEN_EVENT")
        : undefined;
      const includesHealthCheckRaw = getCheckedFilter(
        newFiltersArray,
        "includesHealthCheck",
      )?.value;
      const includesHealthCheckBoolean = includesHealthCheckRaw === "true";

      setFilters({
        branches:
          branchesChecked && branchesChecked.length > 0
            ? branchesChecked
            : undefined,
        cloningStatuses:
          backendStatuses && backendStatuses.length > 0
            ? backendStatuses.map(
                (status): CloningStatus => status as CloningStatus,
              )
            : undefined,
        includesHealthCheck:
          includesHealthCheckRaw === undefined
            ? undefined
            : includesHealthCheckBoolean,
        nicknames:
          nicknamesChecked && nicknamesChecked.length > 0
            ? nicknamesChecked
            : undefined,
        state:
          stateValue === undefined ? undefined : (stateValue as ResourceState),
      });
    },
    [loading, setFilters],
  );

  return useFilters({
    getValues: handleGetValues,
    onFiltersChange: handleFilterChange,
    options,
    setValues: handleSetValues,
  });
};

export { useGitRootsFilters };
